import { useState } from "react";
import { motion } from "framer-motion";
import { 
  Search, 
  ShieldCheck, 
  ShieldAlert, 
  Loader2, 
  UserCheck, 
  CreditCard, 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  MapPin 
} from "lucide-react";
import ProviderLayout from "@/layouts/ProviderLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCheckMember } from "@/lib/api-client-react";

export default function CheckMember() {
  const [searchMode, setSearchMode] = useState<"id" | "personal">("id");
  
  // Single ID / Query search
  const [memberNumber, setMemberNumber] = useState("");

  // Structured Personal Info Search
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");

  const [memberResult, setMemberResult] = useState<any>(null);

  const checkMemberMutation = useCheckMember();

  const handleVerify = async () => {
    try {
      let payload: any = {};
      if (searchMode === "id") {
        if (!memberNumber.trim()) return;
        payload = memberNumber.trim();
      } else {
        if (!firstName.trim() && !lastName.trim() && !email.trim() && !phone.trim() && !dob.trim()) {
          return;
        }
        payload = {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          phone: phone.trim(),
          dob: dob.trim(),
        };
      }

      const data = await checkMemberMutation.mutateAsync(payload);
      if (data.active && data.members && data.members.length > 0) {
        setMemberResult({
          active: true,
          count: data.count || data.members.length,
          members: data.members
        });
      } else if (data.active) {
        setMemberResult({
          active: true,
          count: 1,
          members: [{
            memberId: data.memberId || 'DC-MEMBER',
            name: data.name,
            email: data.email || 'N/A',
            phone: data.phone || 'N/A',
            dob: data.dob,
            joinedDate: data.joinedDate,
            address: data.address
          }]
        });
      } else {
        setMemberResult({ active: false, members: [] });
      }
    } catch (e) {
      setMemberResult({ active: false, members: [] });
    }
  };

  const handleReset = () => {
    setMemberNumber("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setDob("");
    setMemberResult(null);
  };

  const isSearchDisabled = checkMemberMutation.isPending || (
    searchMode === "id" 
      ? !memberNumber.trim() 
      : (!firstName.trim() && !lastName.trim() && !email.trim() && !phone.trim() && !dob.trim())
  );

  return (
    <ProviderLayout>
      <div className="w-full p-6 sm:p-8 lg:p-10 xl:px-12 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/10 rounded-xl flex-shrink-0">
              <UserCheck className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Verify Membership</h1>
              <p className="text-slate-500 text-sm mt-1">
                Verify if a patient is an active StraightDoc member using their Member ID or Personal Details.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
          <div className="space-y-6">
            {/* Search Mode Switcher Tabs */}
            <div className="flex flex-wrap items-center gap-2 p-1.5 bg-slate-100 dark:bg-slate-800/60 rounded-xl w-fit">
              <button
                type="button"
                onClick={() => { setSearchMode("id"); setMemberResult(null); }}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                  searchMode === "id"
                    ? "bg-white dark:bg-slate-900 text-primary shadow-sm"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                <CreditCard className="w-4 h-4" />
                Member ID
              </button>
              <button
                type="button"
                onClick={() => { setSearchMode("personal"); setMemberResult(null); }}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                  searchMode === "personal"
                    ? "bg-white dark:bg-slate-900 text-primary shadow-sm"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                <User className="w-4 h-4" />
                Personal Information
              </button>
            </div>

            {/* Mode 1: Search by Member ID */}
            {searchMode === "id" && (
              <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}>
                <label htmlFor="memberNumber" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Member ID
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="memberNumber"
                      type="text"
                      placeholder="e.g. DC-12345678"
                      value={memberNumber}
                      onChange={(e) => setMemberNumber(e.target.value)}
                      className="pl-9 h-12 text-base"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !isSearchDisabled) {
                          handleVerify();
                        }
                      }}
                    />
                  </div>
                  <Button 
                    onClick={handleVerify}
                    disabled={isSearchDisabled}
                    className="h-12 px-8 bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-sm w-full sm:w-auto"
                  >
                    {checkMemberMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                    {checkMemberMutation.isPending ? "Verifying..." : "Verify Membership"}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Mode 2: Search by Personal Information */}
            {searchMode === "personal" && (
              <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                <p className="text-xs text-slate-500">Provide one or more patient details to lookup active membership status:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">First Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="text"
                        placeholder="e.g. Jane"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="pl-9 h-11"
                        onKeyDown={(e) => e.key === 'Enter' && !isSearchDisabled && handleVerify()}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">Last Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="text"
                        placeholder="e.g. Doe"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="pl-9 h-11"
                        onKeyDown={(e) => e.key === 'Enter' && !isSearchDisabled && handleVerify()}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="email"
                        placeholder="e.g. jane.doe@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-9 h-11"
                        onKeyDown={(e) => e.key === 'Enter' && !isSearchDisabled && handleVerify()}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">Phone Number</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="text"
                        placeholder="e.g. 555-0192"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="pl-9 h-11"
                        onKeyDown={(e) => e.key === 'Enter' && !isSearchDisabled && handleVerify()}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">Date of Birth</label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="date"
                        value={dob}
                        onChange={(e) => setDob(e.target.value)}
                        className="pl-9 h-11"
                        onKeyDown={(e) => e.key === 'Enter' && !isSearchDisabled && handleVerify()}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={handleReset}
                    type="button"
                    className="h-11 px-5"
                  >
                    Clear Fields
                  </Button>
                  <Button 
                    onClick={handleVerify}
                    disabled={isSearchDisabled}
                    className="h-11 px-8 bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-sm"
                  >
                    {checkMemberMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                    {checkMemberMutation.isPending ? "Searching..." : "Verify Membership"}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Results Section */}
            {memberResult && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4 pt-2"
              >
                {memberResult.active && memberResult.members && memberResult.members.length > 0 ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-emerald-600" />
                        Found {memberResult.count} Active {memberResult.count === 1 ? 'Member' : 'Members'}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                      {memberResult.members.map((m: any, idx: number) => (
                        <div 
                          key={m.id || idx}
                          className="p-6 rounded-2xl border-2 bg-emerald-50/50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-900/50 shadow-[0_4px_12px_rgba(16,185,129,0.05)] flex flex-col sm:flex-row gap-5 items-start"
                        >
                          <div className="w-14 h-14 rounded-2xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center flex-shrink-0 text-emerald-600 dark:text-emerald-400 font-bold text-lg">
                            <ShieldCheck className="w-7 h-7" />
                          </div>
                          
                          <div className="flex-1 min-w-0 w-full">
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="text-xl font-bold text-slate-900 dark:text-white">{m.name}</h4>
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/80 dark:text-emerald-300">
                                    ACTIVE MEMBER
                                  </span>
                                </div>
                                {m.memberId && (
                                  <p className="text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400 mt-0.5">
                                    {m.memberId}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 bg-white/70 dark:bg-black/30 p-4 rounded-xl border border-emerald-100 dark:border-emerald-900/40">
                              <div>
                                <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1 flex items-center gap-1">
                                  <Mail className="w-3 h-3 text-slate-400" /> Email
                                </p>
                                <p className="font-medium text-slate-800 dark:text-slate-200 text-sm truncate">{m.email || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1 flex items-center gap-1">
                                  <Phone className="w-3 h-3 text-slate-400" /> Phone
                                </p>
                                <p className="font-medium text-slate-800 dark:text-slate-200 text-sm">{m.phone || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1 flex items-center gap-1">
                                  <Calendar className="w-3 h-3 text-slate-400" /> Date of Birth
                                </p>
                                <p className="font-medium text-slate-800 dark:text-slate-200 text-sm">
                                  {m.dob ? new Date(m.dob).toLocaleDateString() : 'N/A'}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1 flex items-center gap-1">
                                  <MapPin className="w-3 h-3 text-slate-400" /> Address
                                </p>
                                <p className="font-medium text-slate-800 dark:text-slate-200 text-sm truncate">{m.address || 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="p-6 rounded-2xl border-2 bg-red-50/50 border-red-200 dark:bg-red-950/20 dark:border-red-900/50 shadow-[0_4px_12px_rgba(239,68,68,0.05)] flex flex-col sm:flex-row gap-5 items-start">
                    <div className="w-14 h-14 rounded-2xl bg-red-100 dark:bg-red-900/50 flex items-center justify-center flex-shrink-0">
                      <ShieldAlert className="w-7 h-7 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-red-800 dark:text-red-300">Member Not Found or Inactive</h4>
                      <p className="mt-2 text-base text-red-700 dark:text-red-400/90 leading-relaxed">
                        We couldn't find an active StraightDoc membership matching the search criteria provided. Please verify the Member ID or patient details and try again.
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </ProviderLayout>
  );
}
